import { useEffect } from 'preact/hooks';
import { route } from '../state/store.js';
import {
  activePlan,
  planData,
  currentPlanDay,
  todayChapters,
  availablePlans,
  startPlan,
  type PlanId,
  type PlanTestament,
} from '../state/reading-plans-store.js';
import { PlanTile, PLAN_ICON_MAP } from './plan-icons.js';

type Props = {
  onClose: () => void;
};

export function PlansModal({ onClose }: Props) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const active = activePlan.value;
  const day = currentPlanDay.value;
  const chapters = todayChapters.value;

  const onScrim = (e: MouseEvent) => {
    if ((e.target as HTMLElement) === e.currentTarget as HTMLElement) onClose();
  };

  const todayChapter = chapters.length > 0 ? chapters[0] : null;

  const dayOfWeek = new Date().toLocaleDateString('en-US', { weekday: 'long' }).toUpperCase();
  const dayNum = day != null ? day : 1;

  const activeCatalogEntry = active
    ? availablePlans.value.find((p) => p.id === active.id) ?? null
    : null;

  const activePalette = active ? (PLAN_ICON_MAP[active.id]?.pal ?? 'ocean') : 'ocean';

  const navigateTo = (book: string, chapter: number) => {
    route.value = { book, chapter };
    onClose();
  };

  return (
    <div class="modal-scrim" onClick={onScrim}>
      <div class="modal-sheet">
        <div class="modal-sheet-grabber" />
        <div class="modal-sheet-header">
          <span class="modal-sheet-title">Today</span>
          <button type="button" class="modal-sheet-close" onClick={onClose} aria-label="Close">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round">
              <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
        </div>

        <div class="modal-sheet-body">
          {/* Hero cover — palette class drives all colors via CSS vars */}
          <div class={`plan-hero-cover plan--${activePalette}`}>
            <div class="plan-hero__horizon" />
            <div class="plan-hero__sun" />
            <div class="plan-hero-footer">
              <div class="plan-hero-day">{dayOfWeek} · Day {dayNum}</div>
              <div>
                <div class="plan-hero-title">
                  {todayChapter
                    ? `${todayChapter.book} ${todayChapter.chapter}`
                    : activeCatalogEntry?.name ?? 'No active plan'}
                </div>
                <div class="plan-hero-actions">
                  {todayChapter ? (
                    <>
                      <button
                        type="button"
                        class="plan-hero-btn-primary"
                        onClick={() => navigateTo(todayChapter.book, todayChapter.chapter)}
                      >
                        <svg width="11" height="11" viewBox="0 0 24 24" fill="currentColor" stroke="none">
                          <polygon points="5,3 19,12 5,21"/>
                        </svg>
                        LISTEN
                      </button>
                      <button
                        type="button"
                        class="plan-hero-btn-ghost"
                        onClick={() => navigateTo(todayChapter.book, todayChapter.chapter)}
                      >
                        READ
                      </button>
                    </>
                  ) : (
                    <button type="button" class="plan-hero-btn-primary" onClick={onClose}>
                      Browse Plans
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Your plans */}
          <div class="settings-section-label">Your plans</div>
          <PlanRows />

          {/* Discover */}
          <div class="settings-section-label">Discover</div>
          <div class="plan-discover-chips">
            {['Gospels in 30 days', 'Proverbs daily', 'Holy Week', 'Beatitudes'].map((c) => (
              <button key={c} type="button" class="plan-discover-chip">{c}</button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

const TESTAMENT_LABEL: Record<PlanTestament, string> = {
  nt: 'NT', ot: 'OT', both: 'Full',
};

function PlanRows() {
  const active = activePlan.value;
  const day = currentPlanDay.value;
  const data = planData.value;
  const plans = availablePlans.value;

  if (!plans.length) return null;

  return (
    <div>
      {plans.map((p) => {
        const isActive = active?.id === p.id;
        const planDetails = data[p.id];
        const totalDays = planDetails?.totalDays ?? '—';
        const dayLabel = isActive && day != null
          ? `Day ${day} of ${totalDays}`
          : `${totalDays} days`;

        const onRowClick = () => {
          if (!isActive) {
            void startPlan(p.id as PlanId);
          }
        };

        return (
          <div key={p.id} class="plan-row-new" onClick={onRowClick} style={{ cursor: isActive ? 'default' : 'pointer' }}>
            <PlanTile planId={p.id} size={44} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div class="plan-row-title">{p.name}</div>
              <div class="plan-row-sub">
                <span class="plan-row-testament">{TESTAMENT_LABEL[p.testament]}</span>
                {' · '}{dayLabel}
              </div>
            </div>
            {isActive ? (
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--accent, #6b5fb0)" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
                <polyline points="20 6 9 17 4 12"/>
              </svg>
            ) : (
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--muted)" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <polyline points="9 18 15 12 9 6"/>
              </svg>
            )}
          </div>
        );
      })}
    </div>
  );
}
